import React, { useState, useEffect } from 'react';  
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiFileText } from 'react-icons/fi';  
import { mandateAPI, propertyAPI, clientAPI } from '../../services/api';  
import DataTable from '../../components/shared/DataTable';  
import Modal from '../../components/shared/Modal';  
  
const MandateListPage = () => {  
  const navigate = useNavigate();
  const [mandates, setMandates] = useState([]);  
  const [loading, setLoading] = useState(true);  
  const [showModal, setShowModal] = useState(false);  
  const [properties, setProperties] = useState([]);  
  const [clients, setClients] = useState([]);  
  const [filterType, setFilterType] = useState('');  
  const [filterStatus, setFilterStatus] = useState('');  
  const [form, setForm] = useState({  
    property_id: '', client_id: '', type: 'sale', start_date: '', end_date: '',  
    commission_rate: '', exclusive: false, terms: '', notes: '',  
  });  
  
  const fetchMandates = () => mandateAPI.list({ type: filterType, status: filterStatus })  
    .then(r => { setMandates(r.data); setLoading(false); }).catch(() => setLoading(false));  
  
  useEffect(() => { fetchMandates(); }, [filterType, filterStatus]);  
  
  const openCreate = async () => {  
    try {  
      const [pRes, cRes] = await Promise.all([
        propertyAPI.list({ limit: 100 }),
        clientAPI.list({ limit: 100, client_category: 'owner' }),
      ]);  
      setProperties(pRes.data.data || pRes.data || []);  
      setClients(cRes.data || []);  
    } catch (e) {}  
    setShowModal(true);  
  };  
  
  const handleCreate = async (e) => {  
    e.preventDefault();  
    try {  
      await mandateAPI.create(form);  
      setShowModal(false);  
      setForm({  
        property_id: '', client_id: '', type: 'sale', start_date: '', end_date: '',  
        commission_rate: '', exclusive: false, terms: '', notes: '',  
      });
      fetchMandates();  
    } catch (err) { alert(err.response?.data?.error || 'Erreur lors de la création du mandat'); }  
  };  
  
  const typeLabels = { sale: 'Vente', rental: 'Location', management: 'Gestion' };  
  const statusColors = { active: 'success', expired: 'warning', cancelled: 'danger', completed: 'info' };  
  
  const columns = [  
    { header: 'N° Mandat', accessor: 'mandate_number' },  
    { header: 'Type', render: r => <span className="badge badge-info">{typeLabels[r.type] || r.type}</span> },  
    { header: 'Bien', accessor: 'property_title' },  
    { header: 'Client (Propriétaire)', render: r => r.client_first_name ? (
      <span>
        {r.client_first_name} {r.client_last_name}
        <span style={{
          marginLeft: 6, background: '#7c3aed22', color: '#7c3aed',
          border: '1px solid #7c3aed55', borderRadius: 999,
          padding: '1px 7px', fontSize: 11, fontWeight: 600,
        }}>Propriétaire</span>
      </span>
    ) : '-' },  
    { header: 'Début', render: r => new Date(r.start_date).toLocaleDateString('fr-FR') },  
    { header: 'Fin', render: r => r.end_date ? new Date(r.end_date).toLocaleDateString('fr-FR') : 'Indéterminée' },  
    { header: 'Commission', render: r => r.commission_rate ? `${r.commission_rate}%` : '-' },  
    { header: 'Exclusif', render: r => r.exclusive ? <span className="badge badge-warning">Oui</span> : 'Non' },  
    { header: 'Statut', render: r => <span className={`badge badge-${statusColors[r.status] || 'secondary'}`}>{r.status}</span> },  
  ];  
  
  return (  
    <div>  
      <div className="page-header">  
        <h1>Mandats</h1>  
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Nouveau mandat</button>  
      </div>  
      <div className="filters">  
        <select className="form-control" style={{ width: 160 }} value={filterType} onChange={e => setFilterType(e.target.value)}>  
          <option value="">Tous les types</option>  
          <option value="sale">Vente</option>  
          <option value="rental">Location</option>  
          <option value="management">Gestion</option>  
        </select>  
        <select className="form-control" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>  
          <option value="">Tous les statuts</option>  
          <option value="active">Actif</option>  
          <option value="expired">Expiré</option>  
          <option value="cancelled">Annulé</option>  
          <option value="completed">Complété</option>  
        </select>  
      </div>  
      {loading ? <div className="loading"><div className="spinner" /></div> : (  
        <div className="card">
          <DataTable 
            columns={columns} 
            data={mandates} 
            onRowClick={(r) => navigate(`/mandates/${r.id}`)}
            emptyMessage="Aucun mandat" 
          />
        </div>  
      )}  
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nouveau mandat" size="lg">  
        <form onSubmit={handleCreate}>  
          <div className="grid-2">  
            <div className="form-group"><label>Type *</label>  
              <select className="form-control" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>  
                <option value="sale">Vente</option>  
                <option value="rental">Location</option>  
                <option value="management">Gestion</option>  
              </select>  
            </div>  
            <div className="form-group"><label>Bien</label>  
              <select className="form-control" value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })}>  
                <option value="">Sélectionner</option>  
                {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}  
              </select>  
            </div>  
          </div>  
          <div className="grid-2">  
            <div className="form-group"><label>Client propriétaire *</label>  
              <select className="form-control" value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} required>  
                <option value="">-- Sélectionner un propriétaire --</option>  
                {clients.length === 0 && (
                  <option disabled>Aucun client de catégorie "Propriétaire" trouvé</option>
                )}
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.phone ? ` — ${c.phone}` : ''}</option>)}  
              </select>
              <small style={{ color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                Seuls les clients de catégorie <strong>Propriétaire</strong> sont affichés.
              </small>
            </div>  
            <div className="form-group"><label>Commission (%)</label>  
              <input type="number" step="0.01" className="form-control" value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: e.target.value })} />  
            </div>  
          </div>  
          <div className="grid-2">  
            <div className="form-group"><label>Date début *</label>  
              <input type="date" className="form-control" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required />  
            </div>  
            <div className="form-group"><label>Date fin</label>  
              <input type="date" className="form-control" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />  
            </div>  
          </div>  
          <div className="form-group">  
            <label><input type="checkbox" checked={form.exclusive} onChange={e => setForm({ ...form, exclusive: e.target.checked })} style={{ marginRight: 8 }} />Mandat exclusif</label>  
          </div>  
          <div className="form-group">
            <label>Termes, Conditions & Clauses particulières du mandat</label>  
            <textarea 
              className="form-control" 
              rows={3}
              placeholder="Ex: Conditions de reddition des comptes, obligations de gestion, durée de validité, exclusivité publicitaire..." 
              value={form.terms} 
              onChange={e => setForm({ ...form, terms: e.target.value })} 
            />
            <small style={{ color: 'var(--text-secondary)' }}>
              Ces termes et clauses figureront explicitement dans le contrat officiel de mandat avec le propriétaire.
            </small>
          </div>
          <div className="form-group"><label>Notes internes</label>  
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />  
          </div>  
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>  
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>  
            <button type="submit" className="btn btn-primary">Créer le mandat</button>  
          </div>  
        </form>  
      </Modal>  
    </div>  
  );  
};  
  
export default MandateListPage;
